/**
 * In-app inventory management page. Wraps the shared
 * `InventoryActionPanel` with the full app shell. Admins operate here
 * with the same UX as warehouse kiosks — the panel is the single source
 * of truth for inventory management UI.
 *
 * Differences from the kiosk:
 *   - Storage is selectable (admin can pick any storage in the group)
 *   - Barcode print labels are available when `barcode_config.enabled`
 *     and the user has the role
 *   - Posts to `/inventory/adjust/` with JWT
 */
import React, { useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { heIL } from '@mui/x-data-grid-pro/locales';
import { useQuery } from '@tanstack/react-query';

import API from '../services/api';
import { useUser } from '../contexts/UserContext';
import InventoryActionPanel from './components/InventoryActionPanel';

const theme = createTheme({ direction: 'rtl', ...heIL });

const fetchStorages = async () => {
  const response = await API.get('/storages/');
  return response.data;
};

const InventoryActionPage = () => {
  const { userProfile } = useUser();
  const role = userProfile?.role;
  const storageEnabled =
    userProfile?.group?.order_config?.storage_config?.enabled === true;
  const barcodeEnabled = userProfile?.group?.barcode_config?.enabled === true;

  const pageCfg = userProfile?.ui_config?.pages_configs?.InventoryActionPage || {};
  const allowedModes = useMemo(
    () => pageCfg.allowed_modes || ['receive', 'deduct', 'count'],
    [pageCfg.allowed_modes]
  );
  const canPrintLabels = (pageCfg.print_barcode_roles || []).includes(role);

  const { data: storages = [], isLoading: storagesLoading } = useQuery({
    queryKey: ['storages', 'inventory-action-page'],
    queryFn: fetchStorages,
    enabled: storageEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const panelProps = useMemo(
    () => ({
      storageId: null,
      storages,
      allowedModes,
      apiClient: API,
      barcodeEnabled,
      adjustEndpoint: '/inventory/adjust/',
      lookupEndpoint: '/barcode/lookup/',
      productAutocompleteEndpoint: '/products/',
      canPrintLabels: canPrintLabels && barcodeEnabled,
      productLabel: 'מוצר',
      cameraScanEnabled: barcodeEnabled,
    }),
    [storages, allowedModes, barcodeEnabled, canPrintLabels]
  );

  if (!storageEnabled) {
    return (
      <ThemeProvider theme={theme}>
        <Container sx={{ mt: 4 }}>
          <Alert severity="warning">
            ניהול מלאי אינו מופעל עבור הקבוצה הזו. פנה למנהל המערכת כדי להפעיל.
          </Alert>
        </Container>
      </ThemeProvider>
    );
  }

  if (storagesLoading) {
    return (
      <ThemeProvider theme={theme}>
        <Container sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Container>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <div dir="rtl">
        <Container maxWidth={false} sx={{ width: '100%', px: { xs: 1, sm: 3 }, my: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              פעולת מלאי
            </Typography>
            <Typography variant="body2" color="text.secondary">
              קבלה, לקיחה וספירה של מוצרים במחסן{barcodeEnabled ? ' — עם תמיכה בסריקת ברקוד' : ''}
            </Typography>
          </Box>
          <InventoryActionPanel {...panelProps} />
        </Container>
      </div>
    </ThemeProvider>
  );
};

export default InventoryActionPage;
